/**
 * AppNudge
 *
 * A reusable contextual hint/nudge component that appears near related content.
 * Uses Popper + Grow animation to guide user attention to key actions or information.
 *
 * Design: Spotlight/nudge pattern for onboarding and feature discovery
 * References: AppActionMenu for Popper + Grow pattern
 */

import { type ReactNode } from 'react';
import { Box, Popper, Grow, Paper, ClickAwayListener } from '@mui/material';
import { colors, radius, shadows, spacing } from '@/theme/tokens';
import { usePreferencesStore } from '@/stores';
import { X } from 'lucide-react';

export interface AppNudgeProps {
  /** The message or content to display in the nudge */
  children: ReactNode;

  /** Optional icon to display with the nudge */
  icon?: ReactNode;

  /** Optional title text above the message */
  title?: string;

  /** Popper placement relative to anchor element */
  placement?:
    | 'top'
    | 'bottom'
    | 'left'
    | 'right'
    | 'top-start'
    | 'top-end'
    | 'bottom-start'
    | 'bottom-end'
    | 'left-start'
    | 'left-end'
    | 'right-start'
    | 'right-end';

  /** Whether the nudge is currently visible */
  open: boolean;

  /** Callback when nudge is dismissed */
  onClose: () => void;

  /** Optional HTML element to anchor the nudge to */
  anchorEl?: HTMLElement | null;

  /** Optional button configuration */
  cta?: {
    label: string;
    onClick: () => void;
  };

  /** Optional secondary button (e.g., "Learn more") */
  secondaryCta?: {
    label: string;
    onClick: () => void;
  };

  /** Background color variant */
  variant?: 'default' | 'success' | 'warning' | 'info';

  /** Custom styling */
  sx?: Record<string, unknown>;
}

export function AppNudge({
  children,
  icon,
  title,
  placement = 'top',
  open,
  onClose,
  anchorEl,
  cta,
  secondaryCta,
  variant = 'default',
  sx,
}: AppNudgeProps) {
  const reduceMotion = usePreferencesStore((s) => s.reduceMotion);

  // Color scheme based on variant
  const variantStyles = {
    default: {
      bg: colors.accentLight,
      border: colors.accent,
      icon: colors.accent,
      cta: colors.accent,
    },
    success: {
      bg: '#E8F5E9',
      border: '#4CAF50',
      icon: '#4CAF50',
      cta: '#4CAF50',
    },
    warning: {
      bg: '#FFF8E1',
      border: colors.warning,
      icon: colors.warning,
      cta: colors.warning,
    },
    info: {
      bg: '#E3F2FD',
      border: '#2196F3',
      icon: '#2196F3',
      cta: '#2196F3',
    },
  };

  const variantStyle = variantStyles[variant];

  // Arrow pointing toward the anchor — direction is opposite of placement
  const arrowSide = placement.startsWith('left')
    ? 'right'
    : placement.startsWith('right')
      ? 'left'
      : placement.startsWith('bottom')
        ? 'top'
        : 'bottom';

  const arrowSize = 8;
  const arrowStyle: React.CSSProperties = {
    position: 'absolute',
    width: 0,
    height: 0,
    border: `${arrowSize}px solid transparent`,
  };

  if (arrowSide === 'right') {
    Object.assign(arrowStyle, {
      right: -arrowSize * 2,
      top: '50%',
      transform: 'translateY(-50%)',
      borderLeftColor: variantStyle.border,
      borderRight: 'none',
    });
  } else if (arrowSide === 'left') {
    Object.assign(arrowStyle, {
      left: -arrowSize * 2,
      top: '50%',
      transform: 'translateY(-50%)',
      borderRightColor: variantStyle.border,
      borderLeft: 'none',
    });
  } else if (arrowSide === 'bottom') {
    Object.assign(arrowStyle, {
      bottom: -arrowSize * 2,
      left: '50%',
      transform: 'translateX(-50%)',
      borderTopColor: variantStyle.border,
      borderBottom: 'none',
    });
  } else {
    Object.assign(arrowStyle, {
      top: -arrowSize * 2,
      left: '50%',
      transform: 'translateX(-50%)',
      borderBottomColor: variantStyle.border,
      borderTop: 'none',
    });
  }

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement={placement}
      transition
      style={{ zIndex: 300 }}
      modifiers={[
        {
          name: 'offset',
          options: {
            offset: [0, 12],
          },
        },
        {
          name: 'preventOverflow',
          options: {
            padding: spacing[3],
          },
        },
      ]}
    >
      {({ TransitionProps }) => (
        <Grow
          {...TransitionProps}
          timeout={reduceMotion ? 0 : 200}
          style={{ transformOrigin: '0 0' }}
        >
          <Paper
            elevation={0}
            sx={{
              maxWidth: 300,
              backgroundColor: variantStyle.bg,
              border: `1px solid ${variantStyle.border}`,
              borderRadius: radius.md,
              boxShadow: shadows.lg,
              overflow: 'visible',
              position: 'relative',
              ...sx,
            }}
          >
            {/* Directional arrow pointing toward anchor */}
            <div style={arrowStyle} aria-hidden="true" />
            <ClickAwayListener onClickAway={onClose}>
              <Box sx={{ p: spacing[3] }}>
                {/* Close button */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    mb: title || icon ? spacing[2] : 0,
                  }}
                >
                  <button
                    onClick={onClose}
                    aria-label="Dismiss nudge"
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: colors.textSecondary,
                      padding: 0,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <X size={16} />
                  </button>
                </Box>

                {/* Icon + Title */}
                {(icon || title) && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: spacing[2],
                      mb: spacing[2],
                    }}
                  >
                    {icon && (
                      <Box
                        sx={{
                          color: variantStyle.icon,
                          display: 'flex',
                          alignItems: 'center',
                          flexShrink: 0,
                        }}
                      >
                        {icon}
                      </Box>
                    )}
                    {title && (
                      <p
                        style={{
                          margin: 0,
                          fontWeight: 600,
                          fontSize: '14px',
                          color: colors.textPrimary,
                        }}
                      >
                        {title}
                      </p>
                    )}
                  </Box>
                )}

                {/* Message */}
                <Box
                  sx={{
                    fontSize: '14px',
                    lineHeight: '20px',
                    color: colors.textSecondary,
                    mb: cta || secondaryCta ? spacing[3] : 0,
                  }}
                >
                  {children}
                </Box>

                {/* CTAs */}
                {(cta || secondaryCta) && (
                  <Box
                    sx={{
                      display: 'flex',
                      gap: spacing[2],
                      flexWrap: 'wrap',
                    }}
                  >
                    {cta && (
                      <button
                        onClick={cta.onClick}
                        style={{
                          flex: 1,
                          minWidth: 120,
                          padding: `${spacing[2]} ${spacing[3]}`,
                          backgroundColor: variantStyle.cta,
                          color: 'white',
                          border: 'none',
                          borderRadius: radius.sm,
                          fontSize: '14px',
                          fontWeight: 500,
                          cursor: 'pointer',
                          transition: reduceMotion ? 'none' : 'opacity 150ms ease',
                        }}
                        onMouseEnter={(e) => {
                          if (!reduceMotion) {
                            (e.currentTarget as HTMLButtonElement).style.opacity = '0.9';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!reduceMotion) {
                            (e.currentTarget as HTMLButtonElement).style.opacity = '1';
                          }
                        }}
                      >
                        {cta.label}
                      </button>
                    )}
                    {secondaryCta && (
                      <button
                        onClick={secondaryCta.onClick}
                        style={{
                          flex: 1,
                          minWidth: 120,
                          padding: `${spacing[2]} ${spacing[3]}`,
                          backgroundColor: 'transparent',
                          color: variantStyle.cta,
                          border: `1px solid ${variantStyle.cta}`,
                          borderRadius: radius.sm,
                          fontSize: '14px',
                          fontWeight: 500,
                          cursor: 'pointer',
                          transition: reduceMotion ? 'none' : 'opacity 150ms ease',
                        }}
                        onMouseEnter={(e) => {
                          if (!reduceMotion) {
                            (e.currentTarget as HTMLButtonElement).style.opacity = '0.8';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!reduceMotion) {
                            (e.currentTarget as HTMLButtonElement).style.opacity = '1';
                          }
                        }}
                      >
                        {secondaryCta.label}
                      </button>
                    )}
                  </Box>
                )}
              </Box>
            </ClickAwayListener>
          </Paper>
        </Grow>
      )}
    </Popper>
  );
}
